import {makeAutoObservable} from 'mobx';

import type {CourseApiModel} from '../../../../api/course';
import type {MembershipExpandedCommon} from '../../../../api/membership';
import type {ProgramApiModel} from '../../../../api/program';
import type {CollectionStore} from '../../../../api/utils/collectionStore';
import type {MethodStatus} from '../../../../api/utils/entityStore';
import type {
  Destructible,
  WithBusyManager,
  WithReactionDispose,
} from '../../../../utils/store';

import {courseStore} from '../../../../api/course';
import {membershipExpandCommon} from '../../../../api/membership';
import {editionStore, programStore} from '../../../../api/program';
import {apiRequest} from '../../../../api/utils/apiRequest';
import {createCollectionStore} from '../../../../api/utils/collectionStore';
import {alert} from '../../../../components/alert';
import {membershipAcceptRequested} from '../../../../services/entity-action/membership/membership.accept-requested';
import {membershipDeclineRequested} from '../../../../services/entity-action/membership/membership.decline-requested';
import {not} from '../../../../utils/conditionalUtils';
import {isEditionSigned} from '../../../../utils/entity/edition/isEditionSigned';
import {isProgramGroup} from '../../../../utils/entity/program';
import {getLastEdition} from '../../../../utils/entity/program/getLastEdition';
import {
  emitter,
  WS_RECEIVE_MESSAGE_EVENT,
} from '../../../../utils/eventEmitter';
import {logger} from '../../../../utils/logger';
import {
  AsyncBusyManager,
  ReactionDisposeManager,
} from '../../../../utils/store';

type Config = {
  disableAutoFetch?: boolean;
  onRequestsNumberChange?: (numberOfRequests: number) => void;
  programId: string;
};

type ReasonKey = 'actionProcessing';

type Target = CourseApiModel | ProgramApiModel;

const LIMIT = 20;

export class RequestsStore
  implements Destructible, WithBusyManager<ReasonKey>, WithReactionDispose
{
  busy = new AsyncBusyManager<ReasonKey>();
  disposer = new ReactionDisposeManager();
  private readonly membershipsStore: CollectionStore<MembershipExpandedCommon>;
  private readonly selectedMembershipsIds = new Set<string>();

  constructor(private readonly config: Config) {
    this.membershipsStore = createCollectionStore<MembershipExpandedCommon>({
      method: 'coach.memberships.list',
      params: {
        expand: membershipExpandCommon,
        limit: LIMIT,
        query: [
          ['program_id', '==', this.config.programId],
          [
            'status',
            'in',
            ['requested'] as MembershipExpandedCommon['status'][],
          ],
        ],
        sort: [['created', -1]],
      },
      withCount: true,
    });

    makeAutoObservable(this, {}, {autoBind: true});

    emitter.on(WS_RECEIVE_MESSAGE_EVENT, this.handleWSMessage);

    this.busy.register('actionProcessing');

    this.setupReactions();

    if (!this.config.disableAutoFetch) {
      this.fetch();
    }
  }

  destruct(): void {
    emitter.off(WS_RECEIVE_MESSAGE_EVENT, this.handleWSMessage);
    this.membershipsStore.clear();
    this.disposer.dispose();
  }

  get areAllEditionsSignedForSelection() {
    return this.selectedMemberships.every(membership =>
      this.isEditionSignedForMembership(membership),
    );
  }

  get hasMore() {
    return this.membershipsStore.hasMore;
  }

  get isActionInProgress() {
    return this.busy.getReason('actionProcessing') ?? false;
  }

  get isAllSelected() {
    return (
      this.membershipsStore.items.length === this.selectedMembershipsIds.size
    );
  }

  get isEmpty() {
    return this.membershipsStore.isEmpty;
  }

  get isFetching() {
    return this.membershipsStore.isFetching;
  }

  get isLoaded() {
    return this.membershipsStore.isLoaded;
  }

  get memberships() {
    return this.membershipsStore.items;
  }

  get selectedMemberships() {
    return this.membershipsStore.items.filter(membership =>
      this.selectedMembershipsIds.has(membership._id),
    );
  }

  get selectionSize() {
    return this.selectedMembershipsIds.size;
  }

  acceptOne(membership: MembershipExpandedCommon, target?: Target) {
    return this.acceptMemberships([membership], target);
  }

  acceptSelected(target?: Target) {
    return this.acceptMemberships(this.selectedMemberships, target);
  }

  declineOne(membership: MembershipExpandedCommon) {
    return this.declineMemberships([membership]);
  }

  declineSelected() {
    return this.declineMemberships(this.selectedMemberships);
  }

  deselectAll() {
    this.selectedMembershipsIds.clear();
  }

  async fetch(silent = false, offset?: number) {
    try {
      await this.membershipsStore.fetch(
        {
          limit: offset
            ? LIMIT
            : Math.max(this.membershipsStore.items.length, LIMIT),
          offset,
        },
        {
          silent,
        },
      );

      this.config.onRequestsNumberChange?.(this.membershipsStore.count);

      this.removeNotExistedSelectedMembershipIds();
    } catch (e) {
      if (!apiRequest.isCanceledError(e)) {
        logger.error(e);
        alert.error(e);
      }
    }
  }

  async fetchMore() {
    if (this.membershipsStore.hasMore && !this.isFetching) {
      await this.fetch(true, this.membershipsStore.items.length);
    }
  }

  isEditionSignedForMembership(membership: MembershipExpandedCommon) {
    // if the membership for an ind program we need to check the last program edition
    const edition =
      membership.course?.edition || getLastEdition(membership.program);

    return edition ? isEditionSigned(edition) : false;
  }

  isSelected(membershipId: string) {
    return this.selectedMembershipsIds.has(membershipId);
  }

  selectAll() {
    this.membershipsStore.items.forEach(membership => {
      this.selectedMembershipsIds.add(membership._id);
    });
  }

  toggleSelection(membershipId: string) {
    if (this.selectedMembershipsIds.has(membershipId)) {
      this.selectedMembershipsIds.delete(membershipId);
    } else {
      this.selectedMembershipsIds.add(membershipId);
    }
  }

  toggleSelectionAll() {
    if (this.isAllSelected) {
      this.deselectAll();
    } else {
      this.selectAll();
    }
  }

  private async acceptMemberships(
    memberships: MembershipExpandedCommon[],
    target?: Target,
  ) {
    try {
      this.busy.setReason('actionProcessing', true);

      logger.event('request_accept_tap', {
        program_type: isProgramGroup(memberships[0]!.program)
          ? 'group'
          : 'individual',
      });

      await membershipAcceptRequested({memberships, target});

      this.fetch();
    } catch (e) {
      alert.error(e);
    } finally {
      this.busy.setReason('actionProcessing', false);
    }
  }

  private async declineMemberships(memberships: MembershipExpandedCommon[]) {
    try {
      this.busy.setReason('actionProcessing', true);

      const isGroupProgram = isProgramGroup(memberships[0]!.program);

      logger.event('request_decline_tap', {
        program_type: isGroupProgram ? 'group' : 'individual',
      });

      await membershipDeclineRequested({
        isIndividualProgram: not(isGroupProgram),
        memberships,
      });

      this.fetch();
    } catch (e) {
      alert.error(e);
    } finally {
      this.busy.setReason('actionProcessing', false);
    }
  }

  private removeNotExistedSelectedMembershipIds() {
    if (this.selectedMembershipsIds.size) {
      const membershipIds = new Set(
        this.membershipsStore.items.map(membership => membership._id),
      );
      const selectedIds = Array.from(this.selectedMembershipsIds);

      selectedIds.forEach(id => {
        if (!membershipIds.has(id)) {
          this.selectedMembershipsIds.delete(id);
        }
      });
    }
  }

  private setupReactions() {
    this.disposer.watchMany<MethodStatus<unknown>>({
      condition: status => status.success,
      effect: () => this.fetch(true),
      reactionConfigs: [
        {expression: () => programStore.updating, key: 'updatingProgram'},
        {expression: () => editionStore.creating, key: 'creatingEdition'},
        {expression: () => editionStore.updating, key: 'updatingEdition'},
        {expression: () => editionStore.deleting, key: 'deletingEdition'},
        {expression: () => courseStore.updating, key: 'updatingCourse'},
        {expression: () => courseStore.deleting, key: 'deletingCourse'},
      ],
    });
  }

  private handleWSMessage = (message: any) => {
    if (
      message.event.type.includes('membership') &&
      message.membership.program_id === this.config.programId
    ) {
      this.fetch(true);
    }
  };
}
